import { BehaviorSubject, Observable, zip } from 'rxjs';
import { PropertyDTO } from '../DTO/propertyDTO';
import { Address } from './address';
import { PropertyType } from './propertyType';
import { SaleType } from './saleType';
import { Auction } from './auction';
import { PropertyImage } from './propertyImage';
import { fitFromDTO, frameFromDTO } from '../shared/listing-image/listing-image.model';
import { PropertyAttachmentDTO } from '../DTO/propertyAttachmentDTO';
import { PropertyImagePoolDTO } from '../DTO/propertyImagePoolDTO';
import { PropertyService } from '../services/PropertyService';
import { PropertySubType } from './propertySubType';
import { map, take } from 'rxjs/operators';
import { FileData } from '../interfaces/interfaces';
import { Inventory } from './inventory';
import { VaultDocument } from './vaultDocument';
import { DatePipe } from '@angular/common';
import { PropertyPhase } from './propertyPhase';
import { PropertyPrefillDTO } from '../DTO/propertyPrefillDTO';

export class Property{
    public id! : number;
    public propertyId! : string;
    public businessId! : string;
    public inventoryId?:number;
    public clientId?:number;
    public taxYear?:number;
    public header? : string;
    public description? : string;
    public type? : PropertyType;
    public subType? : PropertySubType;
    public address : Address;
    public saleType? : SaleType;
    public auctionRuleId?:number;
    public yearBuilt? : number;
    public annualTaxes? : string;
    public lotSize? : number;
    public parcelId? : string;
    public appraisalAmount? : number;
    public bedrooms? : number;
    public bathroomsFull? : number;
    public bathroomsHalf? : number;
    public garages? : number;
    public carPorts? : number;
    public area? : number;
    public stories? : string;
    public units? : number;
    public unitMix? : UnitMix[];
    public showingsAvailable : boolean = false;
    public latitude? : number;
    public longitude? : number;
    public judgeId? : number;
    public plaintiff? : string;
    public defendant? : string;
    public fileNo? : string;
    public caseNo? : string;
    public clientFileNo? : string;
    public attorneyFileNo? : string;
    public sold : boolean = false;
    public auction? : Auction;
    public propertyImageArray : PropertyImage[]; //public images in public order
    public imagePool? : PropertyImagePoolDTO[]; //all images (property master only)
    public propertyDocumentArray : VaultDocument[];

    /** adds a document (e.g. just executed by the buyer) unless it is listed already; new array so the documents table refreshes */
    public addDocument(vd:VaultDocument){
        if(vd.id && this.propertyDocumentArray.some(d=>d.id===vd.id && d.documentTypeId===vd.documentTypeId)) return;
        this.propertyDocumentArray = [...this.propertyDocumentArray, vd];
    }
    public added? : Date;
    public edited? : Date;
    public visibilitySince? : Date;
    public addedByUserId? : string;
    public editedByUserId? : string;
    public updated? : Date;
    public showSold:boolean = false; //property sold can result in showing it on 'Sold' page. currently property will only show sold if status=6 (ended with winner)
    public additionalAddresses? : Address[];
    public revision? : number;
    public portfolioId? : number;
    public views? : number;
    public commission? : number;
    public commissionValue? : number;
    public salesManagerId? : string; //userId of businessrepresentative users
    public propertyManagerId? : string; //userId of businessrepresentative users
    public assetManagerIds? : string[]; //userId list of client users
    public attorneyIds? : string[]; //userId list of attorney users
    public titleCompany? : string;
    public buyerId? : string; //userid of buyer
    public propertyPhase:PropertyPhase = new PropertyPhase(10, 'Pipeline'); //default to pipeline

    public tag:any; //used to attach a map marker

    public mappingFinishedSubject = new BehaviorSubject(false);

    //property type omitters
    private static omitAttributesResidentialType:string[]=['units','carPorts','unitMix'];
    private static omitAttributesCommercialType:string[]=['bedrooms','carPorts','unitMix'];
    private static omitAttributesMultiFamilyType:string[]=['carPorts','unitMix'];
    private static omitAttributesMultiFamilyType5PlusUnits:string[]=['bedrooms','bathroomsFull','bathroomsHalf'];
    private static omitAttributesLandType:string[]=['bedrooms','bathroomsFull','bathroomsHalf','garages','yearBuilt','area','units','carPorts','unitMix','stories'];
    
    //sale type omitters
    private static omitAttributesCreditorOwnedType:string[]=['appraisalAmount','judge','plaintiff','defendant','caseNo'];
    private static omitAttributesForfeitureRightOfferingType:string[]=['appraisalAmount'];
    private static omitAttributesMortgageForeclosureType:string[]=[];
    private static omitAttributesNonBankOwnedType:string[]=['appraisalAmount'];
    private static omitAttributesPreForeclosureType:string[]=['appraisalAmount'];
    private static omitAttributesReceivershipType:string[]=['appraisalAmount'];
    private static omitAttributesShortSaleType:string[]=['appraisalAmount'];
    private static omitAttributesTaxForeclosureType:string[]=[];
    private static omitAttributesGovernmentOwnedType:string[]=['appraisalAmount'];

    //attributes which are currently mapped to the inventory importing
    public static importableAttributes:string[]=['Header','Description','Property Type','Property Sub-Type','Sale Type','Year Built','Annual Taxes','Lot Size','Parcel Id','Appraisal Amount',
                                                'Bedrooms','Bathrooms Full','Bathrooms Half','Garages','Sqft','Stories','Units','Showings Available','Judge','Plaintiff','File No','Case No','Client File No',
                                                'Attorney File No'];

    private datePipe = new DatePipe('en-US');

    constructor(){
        this.address=new Address();
        this.propertyImageArray = [];
        this.propertyDocumentArray = [];
        this.unitMix = [];
    }

    public static mapToDTO(param:Property):PropertyDTO{
        
        let dto = new PropertyDTO();
            try
            {
                dto.id = param.id;
                dto.propertyId = param.propertyId;
                dto.businessId = param.businessId;
                dto.inventoryId = param.inventoryId;
                dto.clientId = param.clientId;
                dto.taxYear = param.taxYear;
                dto.header=param.header;
                dto.description=param.description;
                dto.typeId=param.type?.id;
                dto.subTypeId=param.subType?.id;
                if(param.address) dto.address= Address.mapToDTO(param.address);
                dto.saleTypeId=param.saleType?.id;
                dto.auctionRuleId=param.auctionRuleId;
                dto.yearBuilt=param.yearBuilt;
                dto.annualTaxes=param.annualTaxes;
                dto.lotSize=param.lotSize;
                dto.parcelId=param.parcelId;
                dto.appraisalAmount=param.appraisalAmount;
                dto.bedrooms=param.bedrooms;
                dto.bathroomsFull=param.bathroomsFull;
                dto.bathroomsHalf=param.bathroomsHalf;
                dto.garages=param.garages;
                dto.carPorts=param.carPorts;
                dto.area=param.area;
                dto.stories=param.stories;
                dto.units=param.units;
                dto.unitMix=JSON.stringify(param.unitMix);
                dto.showingsAvailable=param.showingsAvailable;
                dto.latitude=param.latitude;
                dto.longitude=param.longitude;
                dto.judgeId=param.judgeId;
                dto.plaintiff=param.plaintiff;
                dto.defendant=param.defendant;
                dto.caseNo=param.caseNo;
                dto.fileNo=param.fileNo;
                dto.clientFileNo=param.clientFileNo;
                dto.attorneyFileNo=param.attorneyFileNo;
                dto.showSold=param.showSold;
                dto.sold=param.sold;
                dto.views=param.views;
                if(param.auction) dto.auction=Auction.mapToDTO(param.auction);
                if(param.additionalAddresses) dto.additionalAddresses= param.additionalAddresses.map(a=>Address.mapToDTO(a));
                dto.revision=param.revision;
                if(param.propertyImageArray){
                    param.propertyImageArray.forEach(i=>{
                        var a=new PropertyAttachmentDTO();
                        a.attachmentType=1;
                        a.id=i.id;
                        a.reference=i.reference;
                        a.sequenceNo=i.sequenceNo;
                    });
                }
                if(param.added) dto.added=param.added.toISOString(); 
                if(param.edited) dto.edited=param.edited.toISOString();
                dto.addedByUserId=param.addedByUserId;
                dto.editedByUserId=param.editedByUserId;
                if(param.updated) dto.updated=param.updated.toISOString();
                if(param.visibilitySince) dto.visibilitySince=param.visibilitySince.toISOString();
                dto.portfolioId=param.portfolioId;
                dto.commission=param.commission;
                dto.commissionValue=param.commissionValue;
                dto.salesManagerId=param.salesManagerId;
                dto.propertyManagerId=param.propertyManagerId
                if(param.assetManagerIds) dto.assetManagerIds=param.assetManagerIds.join(',');
                if(param.attorneyIds) dto.attorneyIds=param.attorneyIds.join(',');
                dto.titleCompany=param.titleCompany;
                dto.buyerId=param.buyerId;
                dto.propertyPhaseId=param.propertyPhase?.id;
            }
            catch(e)
            {
                console.log(e);
            }
            return dto;
    }
    public static mapFromDTO(param:PropertyDTO, propertyService:PropertyService):Property
    {
        
        let obj = new Property();
        let o1:Observable<void>;
        let o2:Observable<void>;
        try
        {
            obj.id = param.id;
            obj.propertyId = param.propertyId;
            obj.businessId = param.businessId;
            obj.inventoryId = param.inventoryId;
            obj.clientId = param.clientId;
            obj.taxYear = param.taxYear;
            obj.header = param.header;
            obj.description = param.description;
            o1 = propertyService.getPropertyTypes().pipe(map(pc=>{
                obj.type=pc.find(t=>t.id==param.typeId);
                obj.subType=obj.type?.subTypeList.find(st=>st.id==param.subTypeId);
            }));

            if(param.address) obj.address = Address.mapFromDTO(param.address);
            o2 = propertyService.getSaleTypes().pipe(map(ls=>{
                obj.saleType=ls.find(s=>s.id==param.saleTypeId);
            }));
            obj.auctionRuleId=param.auctionRuleId;
            obj.yearBuilt = param.yearBuilt;
            obj.annualTaxes=param.annualTaxes;
            obj.lotSize=param.lotSize;
            obj.parcelId=param.parcelId;
            obj.appraisalAmount=param.appraisalAmount;
            obj.bedrooms=param.bedrooms;
            obj.bathroomsFull=param.bathroomsFull;
            obj.bathroomsHalf=param.bathroomsHalf;
            obj.garages=param.garages;
            obj.carPorts=param.carPorts;
            obj.area=param.area;
            obj.stories=param.stories;
            obj.units=param.units;
            if(param.unitMix) {
                obj.unitMix=[];
                let arr = <UnitMix[]>JSON.parse(param.unitMix);
                arr?.forEach(item => obj.unitMix!.push(Object.assign(new UnitMix(),item)));
            }
            obj.showingsAvailable = param.showingsAvailable;
            obj.latitude=param.latitude;
            obj.longitude=param.longitude;
            obj.judgeId=param.judgeId;
            obj.plaintiff=param.plaintiff;
            obj.defendant=param.defendant;
            obj.caseNo=param.caseNo;
            obj.fileNo=param.fileNo;
            obj.clientFileNo=param.clientFileNo;
            obj.attorneyFileNo=param.attorneyFileNo;
            obj.showSold=param.showSold;
            obj.sold=param.sold;
            obj.views=param.views;
            if(param.auction) obj.auction=Auction.mapFromDTO(param.auction, propertyService.currentUser?.id);
            if(param.additionalAddresses) obj.additionalAddresses= param.additionalAddresses.map(a=>Address.mapFromDTO(a));
            obj.revision=param.revision;
            zip(o1,o2).pipe(take(1),map(()=>{
                obj.mappingFinishedSubject.next(true);
            })).subscribe();
            if(param.attachments){
                param.attachments.forEach(a=>{
                    if(a.attachmentType==1){
                        let i=new PropertyImage();
                        i.id=a.id;
                        i.reference=a.reference;
                        i.sequenceNo=a.sequenceNo;
                        i.frame=frameFromDTO(a);
                        i.fit=fitFromDTO(a);
                        obj.propertyImageArray.push(i);
                    }
                });
            }
            obj.imagePool=param.imagePool;
            if(param.added) obj.added = new Date(param.added);
            if(param.edited) obj.edited = new Date(param.edited);
            obj.addedByUserId=param.addedByUserId;
            obj.editedByUserId=param.editedByUserId;
            if(param.updated) obj.updated = new Date(param.updated);
            if(param.visibilitySince) obj.visibilitySince = new Date(param.visibilitySince);
            obj.portfolioId=param.portfolioId;
            obj.commission=param.commission;
            obj.commissionValue=param.commissionValue;
            obj.salesManagerId=param.salesManagerId;
            obj.propertyManagerId=param.propertyManagerId
            if(param.assetManagerIds) obj.assetManagerIds=param.assetManagerIds.split(',');
            if(param.attorneyIds) obj.attorneyIds=param.attorneyIds.split(',');
            obj.titleCompany=param.titleCompany;
            obj.buyerId=param.buyerId;
            obj.propertyPhase=propertyService.propertyPhases.find(pp=>pp.id==param.propertyPhaseId) || new PropertyPhase(10, 'Pipeline');
        }
        catch(e)
        {
            console.log(e);
        }
        return obj;
    }
    public static mapFromInventory(i:Inventory, fdArray:FileData[], propertyService:PropertyService):Property{
        if (!fdArray) throw new Error("FileData object is null")
            let obj = new Property();
            let o1:Observable<void>;
            let o2:Observable<void>;
            let o3:Observable<void>;
            try
            {
                obj.businessId = i.businessId;
                obj.inventoryId = i.id;
                obj.clientId = i.clientId;
                obj.taxYear = i.taxYear;
                obj.defendant = i.defendant;
                obj.header = fdArray.find(fd=>fd.key=="Header")?.value;
                obj.description = fdArray.find(fd=>fd.key=="Description")?.value;
                o1 = propertyService.getPropertyTypes().pipe(map(pc=>{
                    obj.type=pc.find(t=>t.name==fdArray.find(fd=>fd.key=="Property Type")?.value);
                    obj.subType=obj.type?.subTypeList.find(st=>st.name==fdArray.find(fd=>fd.key=="Property Sub-Type")?.value);
                }));
                obj.address= i.address.clone;
                o2 = propertyService.getSaleTypes().pipe(map(ls=>{
                    obj.saleType=ls.find(s=>s.name==fdArray.find(fd=>fd.key=="Sale Type")?.value);
                }));
                obj.yearBuilt = Number(fdArray.find(fd=>fd.key=="Year Built")?.value) || undefined;
                obj.annualTaxes=fdArray.find(fd=>fd.key=="Annual Taxes")?.value;
                obj.lotSize=Number(fdArray.find(fd=>fd.key=="Lot Size")?.value) || undefined;
                obj.parcelId=fdArray.find(fd=>fd.key=="Parcel Id")?.value;
                obj.appraisalAmount= Number(fdArray.find(fd=>fd.key=="Appraisal Amount")?.value) || undefined;
                obj.bedrooms=Number(fdArray.find(fd=>fd.key=="Bedrooms")?.value) || undefined;
                obj.bathroomsFull=Number(fdArray.find(fd=>fd.key=="Bathrooms Full")?.value) || undefined;
                obj.bathroomsHalf=Number(fdArray.find(fd=>fd.key=="Bathrooms Half")?.value) || undefined;
                obj.garages=Number(fdArray.find(fd=>fd.key=="Garages")?.value) || undefined;
                obj.area=Number(fdArray.find(fd=>fd.key=="Sqft")?.value) || undefined;
                obj.stories=fdArray.find(fd=>fd.key=="Stories")?.value;
                obj.units=Number(fdArray.find(fd=>fd.key=="Units")?.value) || undefined;
                obj.showingsAvailable = fdArray.find(fd=>fd.key=="Showings Available")?.value || undefined
                obj.plaintiff=fdArray.find(fd=>fd.key=="Plaintiff")?.value;
                obj.caseNo=fdArray.find(fd=>fd.key=="Case No")?.value;
                obj.fileNo=fdArray.find(fd=>fd.key=="File No")?.value;
                obj.clientFileNo=fdArray.find(fd=>fd.key=="Client File No")?.value;
                obj.attorneyFileNo=fdArray.find(fd=>fd.key=="Attroney File No")?.value;
                o3 = propertyService.getVaultDocumentsWithFile(undefined,"inventory",i.id,undefined,undefined,false).pipe(map((vdArray:VaultDocument[])=>{
                    vdArray.forEach(vd=>{
                        vd.id=0;
                        vd.context=undefined;
                        vd.contextId=undefined;
                    })
                    obj.propertyDocumentArray=vdArray;
                }));
               
                zip(o1,o2,o3).pipe(take(1),map(()=>{
                    obj.mappingFinishedSubject.next(true);
                })).subscribe();
            }
            catch(e)
            {
                console.log(e);
            }
            return obj;
    }

    public static mapFromPropertyPrefill(param: PropertyPrefillDTO, propertyService: PropertyService): Property {

    let obj = new Property();
    let o1: Observable<void>;
    let o2: Observable<void>; 
    try {
        obj.taxYear = param.taxYear;
        obj.header = param.header;
        obj.description = param.description;

        o1 = propertyService.getPropertyTypes().pipe(map(pc => {
            obj.type = pc.find(t => t.id == param.typeId);
            obj.subType = obj.type?.subTypeList.find(st => st.id == param.subTypeId);
        }));
        o2 = propertyService.getSaleTypes().pipe(map(ls=>{
            obj.saleType=ls.find(s=>s.id==param.saleTypeId);
        }));

        if (param.address) obj.address = Address.mapFromDTO(param.address);

        obj.yearBuilt = param.yearBuilt;
        obj.annualTaxes = param.annualTaxes;
        obj.lotSize = param.lotSize;
        obj.parcelId = param.parcelId;
        obj.appraisalAmount = param.appraisalAmount;
        obj.bedrooms = param.bedrooms;
        obj.bathroomsFull = param.bathroomsFull;
        obj.bathroomsHalf = param.bathroomsHalf;
        obj.garages = param.garages;
        obj.carPorts = param.carPorts;
        obj.area = param.area;
        obj.stories = param.stories?.toString();
        obj.units = param.units;
        // NOTE: param.unitMix is free-text from document extraction, not the structured
        // UnitMix[] JSON format Property.unitMix expects — intentionally not mapped.
        obj.showingsAvailable = param.showingsAvailable;
        obj.latitude = param.latitude;
        obj.longitude = param.longitude;
        // NOTE: param.judgeName is a name string; Property.judgeId is a foreign key.
        // Cannot safely resolve automatically — surface param.judgeName to the user
        // for manual judge selection rather than mapping here.
        obj.plaintiff = param.plaintiff;
        obj.defendant = param.defendant;
        obj.caseNo = param.caseNo;
        obj.fileNo = param.fileNo;
        obj.clientFileNo = param.clientFileNo;
        obj.attorneyFileNo = param.attorneyFileNo;
        zip(o1,o2).pipe(take(1),map(()=>{
            obj.mappingFinishedSubject.next(true);
        })).subscribe();
    }
    catch (e) {
        console.log(e);
    }

    return obj;
}

    public static omitAttributeForPropertyType(pt:PropertyType, pst:PropertySubType, attributeName:string):boolean{
        //if no type given return properties which are never omitted
        if(!pt) return Property.omitAttributesResidentialType.includes(attributeName)||Property.omitAttributesCommercialType.includes(attributeName)||Property.omitAttributesMultiFamilyType.includes(attributeName)||Property.omitAttributesLandType.includes(attributeName);
        let result:boolean=false;
        switch(pt.id){
            case 1:
                //residential
                result = Property.omitAttributesResidentialType.includes(attributeName);
                break;
            case 2:
                //commercial
                result = Property.omitAttributesCommercialType.includes(attributeName);
                break;
            case 3:
                //multi-family
                if(pst?.id==302) result = Property.omitAttributesMultiFamilyType5PlusUnits.includes(attributeName);
                else result = Property.omitAttributesMultiFamilyType.includes(attributeName);
                break;
            case 4:
                //land
                result = Property.omitAttributesLandType.includes(attributeName);
                break;
            default:
                break;
        }
        return result;
    }
    
    public static omitAttributeForSaleType(st:SaleType, attributeName:string):boolean{
        if(!st) return Property.omitAttributesCreditorOwnedType.includes(attributeName)||Property.omitAttributesForfeitureRightOfferingType.includes(attributeName)||Property.omitAttributesMortgageForeclosureType.includes(attributeName)||
            Property.omitAttributesNonBankOwnedType.includes(attributeName)||Property.omitAttributesPreForeclosureType.includes(attributeName)||Property.omitAttributesReceivershipType.includes(attributeName)||
            Property.omitAttributesShortSaleType.includes(attributeName)||Property.omitAttributesTaxForeclosureType.includes(attributeName)||Property.omitAttributesGovernmentOwnedType.includes(attributeName);
        let result:boolean=false;
        switch(st.id){
            case 1:
                //creditor owned
                result = Property.omitAttributesCreditorOwnedType.includes(attributeName);
                break;
            case 2:
                //forfeiture right offering
                result = Property.omitAttributesForfeitureRightOfferingType.includes(attributeName);
                break;
            case 3:
                //mortgage foreclosure
                result = Property.omitAttributesMortgageForeclosureType.includes(attributeName);
                break;
            case 4:
                //non bank owned
                result = Property.omitAttributesNonBankOwnedType.includes(attributeName);
                break;
            case 5:
                //pre foreclosure
                result = Property.omitAttributesPreForeclosureType.includes(attributeName);
                break;
            case 6:
                //receivership
                result = Property.omitAttributesReceivershipType.includes(attributeName);
                break;
            case 7:
                //short sale
                result = Property.omitAttributesShortSaleType.includes(attributeName);
                break;
            case 8:
                //tax foreclosure
                result = Property.omitAttributesTaxForeclosureType.includes(attributeName);
                break;
            case 9:
                //non bank owned
                result = Property.omitAttributesGovernmentOwnedType.includes(attributeName);
                break;
            default:
                break;
        }
        return result;
    }

    get detailsList(){
        let result:{id:number, label:string,value:string}[]=[];
        if(this.formattedAddress) result.push({id:0, label:"Address",value:this.formattedAddress});
        if(this.type) result.push({id:2, label:"Property Type",value:this.type.name+(this.subType?('('+this.subType.name+')'):'')});
        if(this.saleType) result.push({id:3, label:"Sale Type",value:this.saleType.displayName});
        if(this.parcelId) result.push({id:4, label:"Parcel ID", value:this.parcelId});
        if(this.address?.zipCode) result.push({id:5, label:"Zip Code", value:this.address.zipCode});
        if(this.yearBuilt) result.push({id:6, label:"Year Built", value:this.yearBuilt.toString()});
        if(this.area) result.push({id:7, label:"Square Footage",value:this.area.toLocaleString()+ ' sqft'});
        if(this.bedrooms) result.push({id:8, label:"Bedrooms",value:this.bedrooms.toString()});
        if(this.showingsAvailable) result.push({id:9, label:"Showings Available", value:this.showingsAvailable?'Yes':'No'});
        if(this.bathroomsFull || this.bathroomsHalf) result.push({id:10, label:"Bathrooms", value:(this.bathroomsFull?this.bathroomsFull.toString():'0')+'/'+(this.bathroomsHalf?this.bathroomsHalf.toString():'0')});
        if(this.type?.id==1 && this.stories) result.push({id:11, label:"Stories",value:this.stories.toLocaleString()});
        return result
    }

    get detailsListCard(){
        let result:{id:number, label:string,value:string}[]=[];
        switch(this.type?.id){
            case 1:
                //residential
                if(this.bedrooms) result.push({id:1, label:"bd",value:this.bedrooms.toString()});
                if(this.bathroomsFull || this.bathroomsHalf) result.push({id:2, label:"ba", value:(this.bathroomsFull?this.bathroomsFull.toString():'0')+(this.bathroomsHalf?('/'+this.bathroomsHalf.toString()):'')});
                if(this.lotSize) result.push({id:3, label:"acres",value:this.lotSize.toString()});
                if(this.garages)result.push({id:4, label:"garage", value:this.garages.toString()});
                if(this.area) result.push({id:5, label:"sf",value:this.area.toLocaleString()});
                if(this.stories) result.push({id:6, label:"story",value:this.stories.toLocaleString()});
                break;
            case 2:
                if(this.subType?.id==210){
                    //Hospitality
                    if(this.units) result.push({id:0, label:"units",value:this.units.toString()});
                    if(this.lotSize) result.push({id:3, label:"acres",value:this.lotSize.toString()});
                }
                else{
                    if(this.area) result.push({id:5, label:"sf",value:this.area.toLocaleString()});
                    if(this.lotSize) result.push({id:3, label:"acres",value:this.lotSize.toString()});
                }
                break;  
            case 3:
                //multi-family
                if(this.units) result.push({id:0, label:"units",value:this.units.toString()});
                if(this.lotSize) result.push({id:3, label:"acres",value:this.lotSize.toString()});
                break;
            case 4:
                //land
                if(this.lotSize) result.push({id:3, label:"acres",value:this.lotSize.toString()});
                break;
            default:
                break;
        }

        return result.slice(0,4);
    }
    
    get status():string[]{
        //the following will need a different logic, once we know more about status
        let result = new Array<string>(0);
        if(this.auction?.auctionIsLive && this.auction?.auctionEnded){
            result.push("Ended");
        }
        else if(this.auction?.auctionIsLive && this.auction?.auctionType==1){
            result.push("Auction");
        }
        else if(this.auction?.auctionIsLive && this.auction?.auctionType==2){
            result.push("Buy Now");
        }
        else if(this.auction?.auctionIsLive && this.auction?.auctionType==3){
            result.push("Sale");
        }
        else if(this.auction?.auctionIsLive && this.auction?.auctionType==4){
            result.push("Call For Offer");
        }
        else if(this.auction && !this.auction.auctionIsLive && !this.auction.auctionEnded && this.auction.auctionStatus<=1 && this.auction.comingSoon){
            if(this.auction.auctionStarting && !isNaN(this.auction.auctionStarting.getTime()) && this.auction.auctionStarting>new Date(Date.now() - (5 * 60 * 1000))){ //adding 5 minutes tolerance
                result.push("Bidding Starts " + this.datePipe.transform(this.auction.auctionStarting, 'shortDate'));
            }
            else{
                result.push("Coming Soon");
            }
        }
        if(this.auction?.auctionIsCancelled){
            result.push("Cancelled");
        }
        else{
            if(this.auction?.showFeatured){
                result.push("Featured");
            }
            if(this.auction && this.auction.auctionIsLive && this.auction.auctionType==2 && (!this.auction.buyerPremium || this.auction.buyerPremium==0)){
                result.push("No Premium");
            }
            if(this.views && this.views>=100){
                //i would like to have the threshhold read from config and used here. 
                result.push("Hot");
            }
            if(this.auction?.auctionIsLive && this.auction?.currentSchedule?.postponed && this.auction?.currentSchedule.showPostponed){
                result.push("Postponed");
            }
        }
        return result;
    }

    get formattedAddress():string{
        if(!this.address) return '';
        return this.address.streetAddress1 + ', ' + ((this.address.streetAddress2?.length)?(this.address.streetAddress2 +', '):'') + this.address.city + ', ' + this.address.state + ' ' + this.address.zipCode;
    }

    get formattedAddress1():string{
        if(!this.address) return '';
        return this.address.streetAddress1 + ', ' + ((this.address.streetAddress2?.length)?(this.address.streetAddress2 +', '):'');
    }

    get formattedAddress2():string{
        if(!this.address) return '';
        return this.address.city + ', ' + this.address.state + ' ' + this.address.zipCode;
    }

    get searchData():string{
        if(!this.address) return '';
        return this.formattedAddress.toLowerCase() + ',' + this.address.county?.toLowerCase() + ',' + this.propertyId.toLowerCase();
    }

    get searchInfo():string{
        if(!this.address) return '';
        return this.formattedAddress + ',' + this.address.county + ', ID:' +this.id;
    }

    get hasLegalAd():boolean{
        return (this.propertyDocumentArray.find(vd=>vd.documentTypeId==2))?true:false;
    }

    get center(){
         return (this.latitude && this.longitude)? { lat: this.latitude, lng: this.longitude}:undefined;
    }

    get totalBathRooms(){
        return (this.bathroomsFull?this.bathroomsFull:0)+(this.bathroomsHalf?this.bathroomsHalf:0);
    }

    get totalBathRoomsString(){
        return (this.bathroomsFull?this.bathroomsFull:0).toString()+' / '+(this.bathroomsHalf?this.bathroomsHalf:0).toString();
    }

    get propertyHeaderData1(){
        return this.saleType?.displayName + ' <span class=mx-3>|</span>' + this.type?.name;
    }

    get propertyHeaderData2(){
        switch(this.type?.id){
            case 1:
                //residential
                return (this.bedrooms?this.bedrooms:'0') + ' Bed <span class=mx-3>|</span>' + this.totalBathRooms + ' Bath <span class=mx-3>|</span>' + (this.area?this.area:'0') + ' SF <span class=mx-3>|</span>' +  (this.lotSize?this.lotSize:'0') +' Acres';
            case 2:
                //commercial
                return this.subType?.name + ' <span class=mx-3>|</span>' + (this.lotSize?this.lotSize:'0') +' Acres';
            case 3:
                //multi-family
                return (this.units?this.units:'0') + ' Units <span class=mx-3>|</span>' + (this.lotSize?this.lotSize:'0') +' Acres';
            case 4:
                //land
                return this.subType?.name + ' <span class=mx-3>|</span> ' + (this.lotSize?this.lotSize:'0') +' Acres';
                break;
            default:
                return '';
        }
    }

    get markerClassName(){
        return "home";
        /*
        switch(this.type.id){
            case 1:
                return "home";
            case 2:
                return "building";
            case 3:
                return "apartment";
            case 4:
                return "tree";
            default:
                return "default";
        }
        */
    }

    get firstImageReference(){
        return this.propertyImageArray?.length>0?this.propertyImageArray[0].reference:undefined;
    }

    get dashboardMarkerColor(){
        if(this.auction?.auctionIsLive) return '#00D000';
        else return 'transparent';
    }

    public updateFromProperty(p:Property){
        //deep copy
        this.header = p.header;
        this.description = p.description;
        this.type=p.type
        this.address = p.address;
        this.saleType = p.saleType;
        this.yearBuilt = p.yearBuilt;
        this.annualTaxes=p.annualTaxes;
        this.lotSize=p.lotSize;
        this.parcelId=p.parcelId;
        this.appraisalAmount=p.appraisalAmount;
        this.bedrooms=p.bedrooms;
        this.bathroomsFull=p.bathroomsFull;
        this.bathroomsHalf=p.bathroomsHalf;
        this.garages=p.garages;
        this.area=p.area;
        this.showingsAvailable=p.showingsAvailable;
        this.latitude=p.latitude;
        this.longitude=p.longitude;
        this.defendant=p.defendant;
        this.caseNo=p.caseNo;
        this.fileNo=p.fileNo;
        this.clientFileNo=p.clientFileNo;
        this.attorneyFileNo=p.attorneyFileNo;
        this.showSold=p.showSold;
        this.views=p.views;
        this.auction=p.auction;
        this.propertyImageArray=p.propertyImageArray;
        this.imagePool=p.imagePool;
        this.propertyDocumentArray=p.propertyDocumentArray;
        if(p.added) this.added = new Date(p.added);
        if(p.edited) this.edited = new Date(p.edited);
        this.addedByUserId=p.addedByUserId;
        this.editedByUserId=p.editedByUserId;
        if(p.updated) this.updated = new Date(p.updated);
        if(p.visibilitySince) this.visibilitySince = new Date(p.visibilitySince);
        this.portfolioId=p.portfolioId;
        this.commission=p.commission;
        this.commissionValue=p.commissionValue;
        this.salesManagerId=p.salesManagerId;
        this.propertyManagerId=p.propertyManagerId;
        this.assetManagerIds=p.assetManagerIds;
        this.attorneyIds=p.attorneyIds;
        this.titleCompany=p.titleCompany;
        this.buyerId=p.buyerId;
        this.propertyPhase=p.propertyPhase;
    }

    //return center in exchange for a tag (object like a map marker or so)
    public getCenter(tag:any){
        this.tag=tag;
        return this.center;
    }

    static comparerByAuctionTime = (pA: Property, pB: Property) =>{
        if(pA.auction && pB.auction){
            if (pA.auction.auctionIsLive==true && pB.auction.auctionIsLive==true) {
                if(pA.auction.auctionEnding && pB.auction.auctionEnding){
                    return pA.auction.auctionEnding.getTime()-pB.auction.auctionEnding.getTime();
                }
                else return pA.auction.auctionEnding?-1:(pB.auction.auctionEnding?1:0);
            }
            //change for not yet live auction to earliest startdate
            /*
            else if(pA.auction.auctionIsLive==false && pB.auction.auctionIsLive==false){
                if(pA.auction.auctionEnding && pB.auction.auctionEnding){
                    return (Number.MAX_VALUE-pA.auction.auctionEnding.getTime())-(Number.MAX_VALUE-pB.auction.auctionEnding.getTime());
                }
                else return pA.auction.auctionEnding?-1:(pB.auction.auctionEnding?1:0);
            }*/
            else if(pA.auction.auctionIsLive==false && pB.auction.auctionIsLive==false){
                if(pA.auction.auctionStarting && pB.auction.auctionStarting){
                    return pA.auction.auctionStarting.getTime()-pB.auction.auctionStarting.getTime();
                }
                else return pA.auction.auctionStarting?-1:(pB.auction.auctionStarting?1:0);
            }
            else{
                return pA.auction.auctionIsLive==true?-1:1;
            }
        }
        else return pA.auction?-1:(pB.auction?1:0);
    }
}

export class UnitMix{
    public numberOfUnits? : string;
    public bedrooms?: string;
    public bathroomsFull? : string;
    public bathroomsHalf? : string;
    public area? : number;
    public avgRent? : number;

    get displayArea(){
        return this.area?this.area:'N/A'
    }

    get displayAvgRent(){
        return this.avgRent?this.avgRent:'N/A'
    }
}

